import React, { useState } from 'react';
import {
  Workflow,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  Cpu,
  Layers,
  Box,
  Ruler,
  FileCode,
  Wrench,
  ShieldCheck,
  BookOpen,
  Calculator,
  TrendingUp,
  Clock,
  Activity,
  Compass,
  Brain,
  ListChecks
} from 'lucide-react';

/**
 * Human-readable mapping of tool names to manufacturing tasks
 */
const TOOL_METADATA = {
  // Part Overview
  part_overview: {
    title: 'Inspect Part Workspace & Status',
    description: 'Checked part metadata, CAD file existence, active features, and CAM artifacts',
    agent: 'Coordinator',
    icon: Box
  },

  // Feature Recognition
  run_feature_recognition: {
    title: 'Run 3D Feature Recognition',
    description: 'Invoked ML recognition engine to classify features, face topology, and turning/milling geometry',
    agent: 'Geometry Agent',
    icon: Layers
  },
  find_faces: {
    title: 'Locate & Filter CAD Faces',
    description: 'Queried CAD model topological faces matching feature constraints',
    agent: 'Geometry Agent',
    icon: Ruler
  },

  // Geometry & Features
  list_features: {
    title: 'Extract 3D CAD Features',
    description: 'Analyzed STEP CAD topology and extracted features (holes, pockets, slots, faces)',
    agent: 'Geometry Agent',
    icon: Box
  },
  get_feature_details: {
    title: 'Inspect Feature Geometry & Tolerances',
    description: 'Inspected dimensions, bounding volume, wall thicknesses, and hole parameters',
    agent: 'Geometry Agent',
    icon: Ruler
  },
  get_part_features: {
    title: 'Query Verified Part Features',
    description: 'Retrieved feature registry with active engineer corrections applied',
    agent: 'Geometry Agent',
    icon: Layers
  },
  reconcile_drawing_features: {
    title: 'Reconcile 2D Drawing with 3D CAD',
    description: 'Correlated 2D technical drawing GD&T annotations with 3D model geometry',
    agent: 'Geometry Agent',
    icon: FileCode
  },
  apply_feature_correction: {
    title: 'Apply Geometric Feature Correction',
    description: 'Applied topology correction to feature classification or face grouping',
    agent: 'Geometry Agent',
    icon: Wrench
  },
  list_feature_corrections: {
    title: 'Review Active Feature Corrections',
    description: 'Audited active user and shop corrections against part geometry',
    agent: 'Geometry Agent',
    icon: ListChecks
  },

  // DFM
  run_dfm_analysis: {
    title: 'Execute DFM Manufacturability Rules',
    description: 'Evaluated hole depth-to-diameter ratios, internal corner radii, and tool accessibility',
    agent: 'DFM Agent',
    icon: ShieldCheck
  },
  get_dfm_rule_catalog: {
    title: 'Query DFM Rule Standards',
    description: 'Loaded machining constraints, minimum wall rules, and draft standards',
    agent: 'DFM Agent',
    icon: BookOpen
  },
  list_dfm_violations: {
    title: 'Filter DFM Violations & Warnings',
    description: 'Ranked manufacturability issues by severity and machining difficulty',
    agent: 'DFM Agent',
    icon: AlertCircle
  },

  // Costing
  estimate_cost: {
    title: 'Estimate Manufacturing Cost',
    description: 'Calculated raw material volume, machining cycle rates, and unit cost',
    agent: 'Costing Agent',
    icon: Calculator
  },
  compare_quantities: {
    title: 'Compare Production Quantity Tiers',
    description: 'Computed pricing breaks across production volumes (1, 10, 50, 100 pcs)',
    agent: 'Costing Agent',
    icon: TrendingUp
  },
  list_costing_options: {
    title: 'Query Material & Machine Rates',
    description: 'Loaded certified material rates, density, and machine capability database',
    agent: 'Costing Agent',
    icon: Calculator
  },
  calculate_cost_breakdown: {
    title: 'Compute Manufacturing Cost Breakdown',
    description: 'Calculated raw stock cost, CNC machining time, tooling depreciation, and shop overhead',
    agent: 'Costing Agent',
    icon: Calculator
  },
  compare_quantity_tiers: {
    title: 'Evaluate Volume Tier Price Breaks',
    description: 'Amortized NRE setups and fixturing across production quantities (1, 10, 50, 100 pcs)',
    agent: 'Costing Agent',
    icon: TrendingUp
  },
  estimate_setup_and_cycle_cost: {
    title: 'Estimate Setup & Operation Economics',
    description: 'Calculated machine hour rates, setup labor, and cycle times',
    agent: 'Costing Agent',
    icon: Calculator
  },

  // Process Planning
  plan_machining_operations: {
    title: 'Plan CNC Machining Operations & Routing',
    description: 'Determined operation sequences, roughing vs finishing passes, and 3-axis/5-axis setups',
    agent: 'Planning Agent',
    icon: Cpu
  },
  select_tools_for_features: {
    title: 'Select Cutting Tools & End Mills',
    description: 'Matched feature corner radii and depths with tool magazine inventory',
    agent: 'Planning Agent',
    icon: Wrench
  },
  estimate_cycle_times: {
    title: 'Calculate Machining Cycle Times',
    description: 'Calculated material removal rates (MRR), feed rates, and cycle duration',
    agent: 'Planning Agent',
    icon: Clock
  },

  // CAM
  get_cam_operations: {
    title: 'Query CAM Operation Strategies',
    description: 'Retrieved ModuleWorks CAM operations and workholding coordinate systems',
    agent: 'CAM Agent',
    icon: Activity
  },
  calculate_toolpaths: {
    title: 'Generate CNC Toolpaths & Verify Collisions',
    description: 'Simulated cutter trajectories and verified gouge-free clearance',
    agent: 'CAM Agent',
    icon: Compass
  },
  sync_cad_cam: {
    title: 'Synchronize CAD Geometry with CAM Kernel',
    description: 'Pushed feature updates to downstream CAM kernel',
    agent: 'CAM Agent',
    icon: Activity
  },

  // Memory & Preferences
  search_shop_rules: {
    title: 'Query Shop Memory & Preferences',
    description: 'Checked persistent shop constraints, machine specs, and user preferences',
    agent: 'Coordinator',
    icon: Brain
  },
  record_shop_rule: {
    title: 'Record Shop Rule & Preference',
    description: 'Saved constraint or preference to persistent shop memory',
    agent: 'Coordinator',
    icon: Brain
  },

  // Agent Delegations
  delegate_to_geometry: {
    title: 'Delegate Task to Geometry Specialist',
    description: 'Dispatched 3D model parsing and feature recognition',
    agent: 'Coordinator',
    icon: Workflow
  },
  delegate_to_dfm: {
    title: 'Delegate Task to DFM Specialist',
    description: 'Dispatched design for manufacturability rule validation',
    agent: 'Coordinator',
    icon: Workflow
  },
  delegate_to_cost: {
    title: 'Delegate Task to Costing Specialist',
    description: 'Dispatched cost modeling and batch quotation',
    agent: 'Coordinator',
    icon: Workflow
  },
  delegate_to_cam: {
    title: 'Delegate Task to CAM Specialist',
    description: 'Dispatched toolpath planning and CAM setup',
    agent: 'Coordinator',
    icon: Workflow
  }
};

/**
 * Format Agent Specialist name cleanly
 */
function formatAgentName(agent = '') {
  const norm = String(agent).toLowerCase().trim();
  const map = {
    geometry: 'Geometry Agent',
    dfm: 'DFM Agent',
    cost: 'Costing Agent',
    costing: 'Costing Agent',
    cam: 'CAM Agent',
    process_plan: 'Planning Agent',
    planning: 'Planning Agent',
    supervisor: 'Coordinator',
    coordinator: 'Coordinator'
  };
  return map[norm] || `${norm.charAt(0).toUpperCase() + norm.slice(1)} Agent`;
}

/**
 * Helper to get metadata for any tool
 */
function getToolTaskMeta(toolName = '', agentName = '', args = {}) {
  const cleanName = String(toolName).trim();
  const entry = TOOL_METADATA[cleanName];

  if (entry) {
    return {
      title: entry.title,
      description: args?.task || args?.description || args?.query || entry.description,
      agent: agentName ? formatAgentName(agentName) : entry.agent,
      IconComponent: entry.icon
    };
  }

  // Format fallback title
  const humanized = cleanName
    .replace(/^delegate_to_/i, 'Delegate to ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return {
    title: humanized || 'Executed Engineering Task',
    description: args?.task || args?.description || args?.query || 'Executed tool to evaluate manufacturing parameters',
    agent: formatAgentName(agentName || 'Specialist'),
    IconComponent: Workflow
  };
}

/**
 * Color badge based on agent specialist
 */
function getAgentBadgeColor(agentName = '') {
  const norm = String(agentName).toLowerCase();
  if (norm.includes('geometry')) {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }
  if (norm.includes('dfm')) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (norm.includes('cost')) {
    return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  }
  if (norm.includes('cam')) {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (norm.includes('plan')) {
    return 'bg-purple-50 text-purple-700 border-purple-200';
  }
  return 'bg-slate-100 text-slate-700 border-slate-200';
}

function formatOutputContent(val) {
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return val;
      }
    }
    return val;
  }
  if (typeof val === 'object' && val !== null) {
    return JSON.stringify(val, null, 2);
  }
  return String(val ?? '');
}

/**
 * Individual Collapsible Tool Task Item
 */
function TaskItem({ task, isLast }) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);

  const meta = getToolTaskMeta(task.tool, task.agent, task.args);
  const IconComp = meta.IconComponent;
  const isRunning = task.status === 'running';
  const isError = task.status === 'error';
  const hasDetails = (task.args && Object.keys(task.args).length > 0) || task.result != null;

  const handleCopy = (content, section) => {
    if (navigator.clipboard) {
      const textToCopy = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
      navigator.clipboard.writeText(textToCopy);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 1800);
    }
  };

  return (
    <div className="relative pl-6 pb-3 last:pb-1">
      {/* Connecting Timeline Line */}
      {!isLast && (
        <div className="absolute left-[11px] top-6 bottom-0 w-[2px] bg-slate-200/80" />
      )}

      {/* Task Status Bullet */}
      <div className="absolute left-0 top-0.5">
        {isRunning ? (
          <div className="w-6 h-6 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shadow-2xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          </div>
        ) : isError ? (
          <div className="w-6 h-6 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shadow-2xs">
            <AlertCircle className="w-3.5 h-3.5" />
          </div>
        ) : (
          <div className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-2xs">
            <CheckCircle2 className="w-3.5 h-3.5" />
          </div>
        )}
      </div>

      {/* Task Content Card */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-2xs p-3 transition-all hover:border-slate-300">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {/* Title & Agent Tag */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-xs text-slate-900 flex items-center gap-1.5">
                <IconComp className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                {meta.title}
              </span>

              <span className={`px-1.5 py-0.2 rounded text-[10px] font-semibold border ${getAgentBadgeColor(meta.agent)}`}>
                {meta.agent}
              </span>

              {task.tool && (
                <code className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1 py-0.2 rounded">
                  {task.tool}
                </code>
              )}
            </div>

            {/* Description / Purpose */}
            {meta.description && (
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                {meta.description}
              </p>
            )}
          </div>

          {/* Details Toggle Button */}
          {hasDetails && (
            <button
              type="button"
              onClick={() => setIsDetailsOpen(!isDetailsOpen)}
              className="shrink-0 px-2 py-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200/80 transition-colors cursor-pointer flex items-center gap-1"
              title="View parameters and output"
            >
              <span>{isDetailsOpen ? 'Hide' : 'Inspect'}</span>
              {isDetailsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>

        {/* Collapsible Input & Output Inspector */}
        {isDetailsOpen && hasDetails && (
          <div className="mt-3 pt-2.5 border-t border-slate-100 space-y-2.5 text-xs">
            {/* Input Arguments */}
            {task.args && Object.keys(task.args).length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    Parameters & Arguments
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(task.args, 'args')}
                    className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-0.5 cursor-pointer font-normal"
                  >
                    {copiedSection === 'args' ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span className="text-emerald-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy JSON</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="bg-slate-900 text-slate-200 rounded-lg p-2.5 font-mono text-[11px] overflow-x-auto max-h-48">
                  <pre className="whitespace-pre-wrap">{JSON.stringify(task.args, null, 2)}</pre>
                </div>
              </div>
            )}

            {/* Output / Result */}
            {task.result != null && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Result & Tool Response
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(task.result, 'result')}
                    className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-0.5 cursor-pointer font-normal"
                  >
                    {copiedSection === 'result' ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span className="text-emerald-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy JSON</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="bg-slate-900 text-emerald-300 rounded-lg p-2.5 font-mono text-[11px] overflow-x-auto max-h-56">
                  <pre className="whitespace-pre-wrap">{formatOutputContent(task.result)}</pre>
                </div>
              </div>
            )}

            {/* Error Message if any */}
            {task.error && (
              <div className="p-2 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-[11px]">
                <span className="font-semibold">Error: </span>
                {typeof task.error === 'string' ? task.error : JSON.stringify(task.error)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Master Collapsible Agent Tasks Component
 * Interfaced like tasks done by agents to achieve the result
 */
export default function AgentToolTasks({ tasks = [], isGenerating = false }) {
  // Collapsed by default below the chat card touched with gray
  const [isOpen, setIsOpen] = useState(false);

  if (!tasks || tasks.length === 0) {
    return null;
  }

  const completedCount = tasks.filter((t) => t.status === 'completed').length;
  const runningCount = tasks.filter((t) => t.status === 'running').length;
  const isRunning = runningCount > 0 || isGenerating;

  // Extract distinct agents involved in these tasks
  const distinctAgents = Array.from(
    new Set(tasks.map((t) => formatAgentName(t.agent || 'Coordinator')))
  );

  return (
    <div className="border-t border-slate-200/90 bg-slate-50/90 text-xs w-full transition-all">
      {/* Master Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors cursor-pointer select-none"
      >
        {/* Left Side: Status Icon, Title & Agent Badges */}
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          {/* Status Indicator Icon */}
          <div
            className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
              isRunning
                ? 'bg-blue-100 text-blue-600'
                : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {isRunning ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
          </div>

          {/* Heading */}
          <div className="flex items-center gap-2">
            <span className="font-bold text-xs text-slate-800">
              {isRunning
                ? `Executing tasks (${completedCount}/${tasks.length} completed)...`
                : `${tasks.length} task${tasks.length > 1 ? 's' : ''} executed to achieve this result`}
            </span>
          </div>

          {/* Agent Specialist Badges */}
          <div className="hidden sm:flex items-center gap-1">
            {distinctAgents.slice(0, 3).map((agentName) => (
              <span
                key={agentName}
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getAgentBadgeColor(agentName)}`}
              >
                {agentName}
              </span>
            ))}
            {distinctAgents.length > 3 && (
              <span className="text-[10px] text-slate-400 font-medium">
                +{distinctAgents.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Right Side: Toggle Pill */}
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <span className="text-[11px] font-medium text-slate-500">
            {isOpen ? 'Collapse' : `View ${tasks.length} step${tasks.length > 1 ? 's' : ''}`}
          </span>
          <div className="w-5 h-5 rounded-md bg-white border border-slate-200/90 flex items-center justify-center text-slate-500">
            {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </div>
        </div>
      </button>

      {/* Expanded Task Timeline */}
      {isOpen && (
        <div className="p-4 pt-3 border-t border-slate-200/70 bg-slate-100/50">
          <div className="space-y-0.5">
            {tasks.map((task, idx) => (
              <TaskItem
                key={task.id || idx}
                task={task}
                isLast={idx === tasks.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
